/**
 * The project's repo flags, shared by every part of the page that shows them (vfs-service `GET /repo-flags`): a repo
 * (`services/<name>` or `context/<name>`, each its own git repo) can be Read-only (nobody, person or agent, changes,
 * commits or pushes its files) and No build (context only: never built, started or published). vfs-service enforces
 * them; the page only shows them and leaves out what would be refused.
 *
 * One answer per project is kept here and handed to every `useRepoFlags` (hooks/useRepoFlags.js) of that project, so
 * the Apps list, the Code tree and the Start buttons ask once. It is read again on demand (`loadRepoFlags`), when the
 * page gets the focus back (at most every 30 s), and replaced at once by the tree socket's `tree:repo_flags` and by
 * the answer to an own change (`publishRepoFlags`). A vfs-service without flags (404) or any error: nothing is
 * flagged, everything works as before.
 */
import * as vfsClient from './vfsClient';

export const EMPTY_REPO_FLAGS = Object.freeze({ repos: {}, canChange: false, terminal: '', loaded: false });

const entries = new Map(); // projectId -> { state, listeners: Set, loading: Promise|null, loadedAt }

function entryOf(projectId) {
  let entry = entries.get(projectId);
  if (!entry) {
    entry = { state: EMPTY_REPO_FLAGS, listeners: new Set(), loading: null, loadedAt: 0 };
    entries.set(projectId, entry);
  }
  return entry;
}

function set(projectId, next) {
  const entry = entryOf(projectId);
  entry.state = next;
  entry.listeners.forEach((listener) => listener(next));
}

/** What is known now of a project's flags (EMPTY_REPO_FLAGS before the first answer). */
export function repoFlagsState(projectId) {
  return projectId ? entryOf(projectId).state : EMPTY_REPO_FLAGS;
}

/** Calls `listener(state)` on every change of the project's flags; returns the unsubscribe. */
export function subscribeRepoFlags(projectId, listener) {
  const entry = entryOf(projectId);
  entry.listeners.add(listener);
  return () => { entry.listeners.delete(listener); };
}

/**
 * Reads the project's flags from vfs-service (one request at a time per project). `maxAgeMs`: skip it when the last
 * answer is younger. Resolves to the state; never rejects (an error keeps what was known, or nothing flagged).
 */
export async function loadRepoFlags(projectId, { maxAgeMs = 0 } = {}) {
  if (!projectId) { return EMPTY_REPO_FLAGS; }
  const entry = entryOf(projectId);
  if (entry.loading) { return entry.loading; }
  if (maxAgeMs && entry.loadedAt && Date.now() - entry.loadedAt < maxAgeMs) { return entry.state; }
  entry.loading = (async() => {
    try {
      const answer = await vfsClient.getRepoFlags(projectId);
      set(projectId, {
        repos: answer?.repos && typeof answer.repos === 'object' ? answer.repos : {},
        canChange: Boolean(answer?.canChange),
        terminal: typeof answer?.terminal === 'string' ? answer.terminal : '',
        loaded: true,
      });
    } catch {
      set(projectId, { ...entry.state, loaded: true });
    } finally {
      entry.loadedAt = Date.now();
      entry.loading = null;
    }
    return entry.state;
  })();
  return entry.loading;
}

/** New flags for every repo (the tree socket's `tree:repo_flags`, a PUT's answer): the rest of the state is kept. */
export function publishRepoFlags(projectId, repos) {
  if (!projectId || !repos || typeof repos !== 'object') { return; }
  set(projectId, { ...repoFlagsState(projectId), repos, loaded: true });
}

/** Forgets every project's flags (tests). */
export function resetRepoFlags() {
  entries.clear();
}

/** The repo a project path is in (`services/api/src/x.js` -> `services/api`), or null (brain/, the root, …). */
export function repoOfPath(path) {
  const parts = String(path || '').replace(/^\.?\/+/, '').split('/').filter(Boolean);
  if (parts.length < 2 || (parts[0] !== 'services' && parts[0] !== 'context')) { return null; }
  return `${parts[0]}/${parts[1]}`;
}

/** A service's repo key from its name as the runner or the Git tab says it (`api` or `services/api`). */
export function serviceRepo(name) {
  const value = String(name || '').replace(/^\.?\/+|\/+$/g, '');
  if (!value) { return null; }
  return value.includes('/') ? repoOfPath(value) : `services/${value}`;
}

/** The flags of the repo `path` is in, with its key (`{ repo, readOnly, noBuild, context, … }`), or null. */
export function flagsOfPath(repos, path) {
  const repo = repoOfPath(path);
  const flags = repo ? repos?.[repo] : null;
  return flags ? { repo, ...flags } : null;
}

/** Why an edit of `path` is refused (a read-only repo), in words, or null. */
export function readOnlyReason(repos, path) {
  const flags = flagsOfPath(repos, path);
  return flags?.readOnly ? `Read-only: ${flags.repo} is flagged read-only in this project.` : null;
}

/** Whether a service (`api` or `services/api`) is context only: never built or started. Uses what is known now. */
export function isNoBuild(projectId, serviceName) {
  const repo = serviceRepo(serviceName);
  return Boolean(repo && repoFlagsState(projectId).repos?.[repo]?.noBuild);
}

/**
 * The project path of a file open in the editor: a service's files are relative to its folder (`services/api`), the
 * project root's ("." or "Home") already project-relative.
 */
export function projectPathOf(serviceName, filePath) {
  if (!filePath) { return ''; }
  if (!serviceName || serviceName === '.' || serviceName === 'Home') { return filePath; }
  const folder = String(serviceName).includes('/') ? serviceName : `services/${serviceName}`;
  return `${folder.replace(/\/+$/, '')}/${filePath}`;
}
